<template>
  <div :class="[is_small || is_medium || is_flexible_width ? 'flex items-center justify-center' : {}]">
    <div
      class="rounded-3xl! bg-white"
      :class="[container_classes_base, is_mobile ? '' : container_classes_size]"
      data-test="modal"
    >
      <section
        class="flex h-full flex-col text-slate-700"
        :class="top_border_class"
      >
        <div
          v-if="is_show_header"
          class="p-5"
        >
          <div class="text-xl font-semibold">
            <slot name="title">
              {{ $t('modal.labels.areYouSure') }}
            </slot>
          </div>
          <div
            v-if="$slots.subtitle"
            class="mt-1 text-base text-slate-600"
          >
            <slot name="subtitle">
              {{ $t('modal.labels.areYouSure') }}
            </slot>
          </div>
          <div
            v-if="$slots.message"
            class="pt-5"
          >
            <div>
              <slot name="message" />
              <div
                v-if="$slots.sub_message"
                class="mt-4 font-semibold"
              >
                <slot name="sub_message" />
              </div>
            </div>
          </div>
          <div
            v-if="$slots.notification"
            class="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm font-medium"
          >
            <slot name="notification" />
          </div>
          <div
            v-if="$slots.notification_danger"
            class="mt-4 rounded border border-danger-200 bg-danger-50 p-3 text-sm font-medium text-danger-900"
            data-test="notification-danger"
          >
            <slot name="notification_danger" />
          </div>
        </div>
        <div
          v-if="$slots.content"
          class="flex-1 overflow-y-auto p-5"
        >
          <slot name="content" />
        </div>
        <footer
          v-if="is_show_footer"
          class="mt-5 bg-slate-100 p-4"
        >
          <div class="flex flex-wrap items-center gap-2">
            <ButtonPrimary
              v-if="$slots.ButtonPrimary && is_show_buttons"
              :action="action"
              data-test="modal-confirm-primary"
              :is_disabled="!is_confirmed"
              :is_loading="is_loading"
              @onClick="onSubmit"
            >
              <slot name="ButtonPrimary" />
            </ButtonPrimary>
            <ButtonPrimary
              v-if="$slots.ButtonLoading && is_show_buttons"
              is_disabled
              is_loading
            >
              <slot name="ButtonLoading">
                {{ $t('button.actions.loading') }}
              </slot>
            </ButtonPrimary>
            <ButtonDestructive
              v-if="$slots.ButtonDestructive && is_show_buttons"
              filled
              data-test="modal-confirm-primary"
              :is_disabled="!is_confirmed"
              @onClick="onSubmit"
            >
              <slot name="ButtonDestructive">
                {{ $t('button.actions.delete') }}
              </slot>
            </ButtonDestructive>
            <ButtonDestructive
              v-else-if="$slots.ButtonDestructiveLoading && is_show_buttons"
              filled
              data-test="modal-confirm-primary"
              is_disabled
              is_loading
              @onClick="onSubmit"
            >
              <slot name="ButtonDestructive">
                {{ $t('button.actions.loading') }}
              </slot>
            </ButtonDestructive>
            <ButtonSecondary
              v-if="$slots.ButtonSecondary && is_show_buttons"
              data-test="modal-confirm-secondary"
              @onClick="onSecondary"
            >
              <slot name="ButtonSecondary" />
            </ButtonSecondary>
            <ButtonTertiary
              v-if="is_cancel && is_show_buttons"
              data-test="modal-confirm-tertiary"
              @onClick="onCancel"
            />
            <div
              v-else
              class="ml-auto flex items-center justify-end"
            >
              <slot name="content-footer" />
            </div>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, useSlots } from 'vue';

defineOptions({ name: 'Modal' });

const props = defineProps({
  action: { type: String, default: null },
  has_parent: { type: Boolean, default: true },
  is_cancel: { type: Boolean, default: true },
  can_overflow: { type: Boolean },
  is_confirmed: { type: Boolean, default: true },
  is_loading: { type: Boolean },
  is_fixed_height: { type: Boolean },
  is_flexible_width: { type: Boolean },
  is_medium: { type: Boolean },
  is_show_buttons: { type: Boolean, default: true },
  is_show_footer: { type: Boolean, default: true },
  is_show_header: { type: Boolean, default: true },
  is_small: { type: Boolean, default: true },
  prevent_automatic_close: { type: Boolean },
  prevent_enter_shortcut: { type: Boolean },
});

const emit = defineEmits(['actionCancel', 'actionPrimary', 'actionSecondary']);

const slots = useSlots();
const instance = getCurrentInstance();
const app = instance.proxy;

const is_mobile = computed(() => Boolean(app.mq.isMobile));

const top_border_class = computed(() =>
  slots.ButtonDestructive || slots.ButtonDestructiveLoading
    ? 'border-t-4 border-danger-500'
    : 'border-t-4 border-primary-500',
);

const container_classes_base = computed(() => [props.can_overflow ? 'overflow-visible' : 'overflow-hidden']);

const container_classes_size = computed(() => {
  const class_names = [];

  if (props.is_fixed_height) class_names.push('h-[calc(100vh-200px)]');

  if (props.is_flexible_width) {
    class_names.push('w-full');
  } else if (props.is_small) {
    class_names.push('w-[360px]');
  } else if (props.is_medium) {
    class_names.push('w-[600px]');
  }

  return class_names;
});

const close = () => {
  app.$parent.$parent.close();
};

const onCancel = () => {
  emit('actionCancel');
  if (props.has_parent) close();
};

const onSecondary = () => {
  emit('actionSecondary');
};

const onSubmit = () => {
  emit('actionPrimary');
  if (props.has_parent && !props.prevent_automatic_close) close();
};

const onKeydown = (e) => {
  if (props.prevent_enter_shortcut || !props.is_confirmed) return;
  if (e.key === 'Enter') onSubmit();
};

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
});
</script>
<style>
.modal:not(.is-full-screen) .modal-close::before,
.modal:not(.is-full-screen) .modal-close::after {
  background: white !important;
}
</style>
